import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import AuthLayout, {
  authButtonClass,
  authFieldClass,
} from "@/components/AuthLayout";

export default function SignupPage() {
  const router = useRouter();
  const inviteToken =
    typeof router.query.invite === "string" ? router.query.invite : "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgInvite, setOrgInvite] = useState<{
    email: string;
    companyName: string;
  } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      const res = await fetch(`/api/invites/${inviteToken}`);
      if (res.ok) {
        const info = await res.json();
        setOrgInvite(info);
        setEmail(info.email);
      }
    })();
  }, [inviteToken]);

  // Arriving with a generated map (?claim=), template (?template=) or a
  // cloneable share link (?share=): show what's waiting — continuity converts.
  const [pendingMap, setPendingMap] = useState<string | null>(null);
  const claimId =
    typeof router.query.claim === "string" ? router.query.claim : "";
  const templateSlug =
    typeof router.query.template === "string" ? router.query.template : "";
  const shareToken =
    typeof router.query.share === "string" ? router.query.share : "";
  React.useEffect(() => {
    const src = /^[a-f0-9-]{36}$/i.test(claimId)
      ? `/api/tools/generated/${claimId}`
      : /^[a-z0-9-]+$/.test(templateSlug)
      ? `/api/templates/${templateSlug}`
      : /^[a-f0-9]{32}$/.test(shareToken)
      ? // No ?claim=1 here — the count belongs to the actual clone, not to
        // everyone who merely lands on the signup form.
        `/api/shared/${shareToken}`
      : null;
    if (!src) return;
    (async () => {
      const res = await fetch(src);
      if (res.ok) {
        const info = await res.json();
        if (info?.name) setPendingMap(String(info.name));
      }
    })();
  }, [claimId, templateSlug, shareToken]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          password,
          inviteToken: inviteToken || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || "Could not create your account.");
        setBusy(false);
        return;
      }
      // Auto sign-in after signup.
      const login = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });
      setBusy(false);
      if (login?.error) {
        router.push("/login");
        return;
      }
      // Marketing pricing CTAs arrive with ?plan= — go straight to checkout.
      const plan = typeof router.query.plan === "string" ? router.query.plan : "";
      if (["starter", "pro", "scale"].includes(plan)) {
        router.push(`/company?checkout=${plan}_monthly`);
        return;
      }
      // Template gallery CTAs arrive with ?template= — clone it on landing.
      const template =
        typeof router.query.template === "string" ? router.query.template : "";
      if (/^[a-z0-9-]+$/.test(template)) {
        router.push(`/?template=${template}`);
        return;
      }
      // The free funnel generator arrives with ?claim=<generated map id>.
      const claim =
        typeof router.query.claim === "string" ? router.query.claim : "";
      if (/^[a-f0-9-]{36}$/i.test(claim)) {
        router.push(`/?claim=${claim}`);
        return;
      }
      // A cloneable share link arrives with ?share=<token>.
      const share =
        typeof router.query.share === "string" ? router.query.share : "";
      if (/^[a-f0-9]{32}$/.test(share)) {
        router.push(`/?share=${share}`);
        return;
      }
      router.push("/");
    } catch {
      setBusy(false);
      setError("Something went wrong. Try again.");
    }
  };

  return (
    <AuthLayout title="Create account">
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">
        Create your account
      </h2>
      <p className="mt-1.5 text-[14px] text-slate-500">
        Start mapping and automating in minutes.
      </p>
      {orgInvite && (
        <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-[13px] text-indigo-700">
          You&apos;re joining{" "}
          <strong>{orgInvite.companyName}</strong> — sign up with{" "}
          <strong>{orgInvite.email}</strong> and you&apos;ll land right in the
          team.
        </div>
      )}
      {pendingMap && (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800">
          🗺 <strong>{pendingMap}</strong> is ready — it&apos;ll be on your
          canvas the moment you&apos;re in. Free plan, no card.
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
            Name
          </label>
          <input
            required
            autoFocus
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jason Casey"
            className={authFieldClass}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
            Email
          </label>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            readOnly={Boolean(orgInvite)}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className={`${authFieldClass} ${
              orgInvite ? "bg-slate-50 text-slate-500" : ""
            }`}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-slate-700">
            Password
          </label>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className={authFieldClass}
          />
        </div>
        <p className="text-[12px] leading-snug text-slate-400">
          By creating an account you agree to the{" "}
          <a
            href="https://www.frazzlecrm.com/terms"
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://www.frazzlecrm.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 hover:underline"
          >
            Privacy Policy
          </a>
          .
        </p>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-600">
            {error}
          </div>
        )}

        <button type="submit" disabled={busy} className={authButtonClass}>
          {busy ? "Creating account..." : "Create account"}
        </button>
      </form>

      <p className="mt-6 text-center text-[13px] text-slate-500">
        Already have an account?{" "}
        <Link
          href={
            /^[a-f0-9]{32}$/.test(shareToken)
              ? // Keep the waiting map through a sign-in too — the person a
                // map gets shared with often already has an account.
                `/login?callbackUrl=${encodeURIComponent(
                  `/?share=${shareToken}`
                )}`
              : "/login"
          }
          className="font-semibold text-indigo-600 hover:text-indigo-700"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
